import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/adminAPI';
import { useToast } from '../../hooks/use-toast';
import { Check, X, Eye, Filter, ShieldCheck } from 'lucide-react';

const AdminKYC = () => {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [reason, setReason] = useState('');
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try { setRows(await adminAPI.kycList(statusFilter || undefined)); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const view = async (userId) => {
    try { setDetail(await adminAPI.kycDetail(userId)); setReason(''); } catch {}
  };

  const review = async (userId, action) => {
    try {
      await adminAPI.kycReview(userId, action, action === 'reject' ? reason : undefined);
      toast({ title: `KYC ${action}d` });
      setDetail(null); setReason('');
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3"><ShieldCheck size={22} className="text-[#00e676]"/> KYC Verification</h1>
          <p className="text-sm text-gray-400">Review user identity documents and approve or reject.</p>
        </div>
        <div className="flex items-center gap-1">
          <Filter size={13} className="text-gray-500 mr-1"/>
          {['pending', 'approved', 'rejected', ''].map(s => (
            <button key={s || 'all'} onClick={() => setStatusFilter(s)} className={`px-2.5 py-1 rounded text-[11px] font-semibold ${statusFilter === s ? 'bg-[#00e676] text-black' : 'border border-[#1a2823] text-gray-400 hover:border-[#00e676]/40'}`}>{s || 'All'}</button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">User</th><th>Full Name</th><th>ID Type</th><th>Country</th><th>Submitted</th><th>Status</th><th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="py-6 text-center text-sm text-gray-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan="7" className="py-10 text-center text-sm text-gray-500">No submissions found</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-[#141f1b] hover:bg-[#0f1a17]">
                  <td className="px-4 py-3">
                    <div className="text-sm text-white font-semibold">{r.user_email}</div>
                    <div className="text-[10px] text-gray-500">{r.user_id.substring(0, 8)}…</div>
                  </td>
                  <td className="text-gray-300">{r.full_name}</td>
                  <td className="text-xs text-gray-400 uppercase">{r.id_type?.replace('_', ' ')}</td>
                  <td className="text-xs text-gray-400">{r.country}</td>
                  <td className="text-xs text-gray-400 mono">{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}</td>
                  <td>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${r.status === 'approved' ? 'bg-[#00e676]/15 text-[#00e676]' : r.status === 'pending' ? 'bg-amber-500/15 text-amber-400' : 'bg-red-500/15 text-red-400'}`}>{r.status}</span>
                  </td>
                  <td className="text-right pr-4">
                    <button onClick={() => view(r.user_id)} className="text-[11px] px-2.5 py-1 rounded border border-[#1a2823] text-gray-300 hover:text-white hover:border-[#00e676]/40 inline-flex items-center gap-1"><Eye size={11}/> Review</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-4xl my-8 p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-white font-bold text-lg">KYC Review: {detail.full_name}</h3>
                <div className="text-xs text-gray-400">{detail.user_email}</div>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-white"><X size={18}/></button>
            </div>

            <div className="grid md:grid-cols-2 gap-4 text-sm mb-5">
              <Field label="Full Name" value={detail.full_name}/>
              <Field label="Date of Birth" value={detail.date_of_birth}/>
              <Field label="Nationality" value={detail.nationality}/>
              <Field label="Phone" value={detail.phone}/>
              <Field label="Address" value={`${detail.address_line1 || ''} ${detail.address_line2 || ''} ${detail.city}, ${detail.state || ''} ${detail.postal_code}, ${detail.country}`}/>
              <Field label="ID Type" value={detail.id_type?.replace('_', ' ')}/>
              <Field label="ID Number" value={detail.id_number}/>
              <Field label="Occupation" value={detail.occupation}/>
              <Field label="Source of Funds" value={detail.source_of_funds}/>
              <Field label="Annual Income" value={detail.annual_income}/>
            </div>

            <h4 className="text-sm font-bold text-white mb-2">Documents</h4>
            <div className="grid md:grid-cols-2 gap-4 mb-5">
              {[
                { key: 'id_front', label: 'ID Front' },
                { key: 'id_back', label: 'ID Back' },
                { key: 'selfie', label: 'Selfie with ID' },
                { key: 'proof_of_address', label: 'Proof of Address' }
              ].map(d => (
                detail[d.key] && detail[d.key].startsWith('data:image') ? (
                  <div key={d.key}>
                    <div className="text-[10px] text-gray-400 uppercase mb-1">{d.label}</div>
                    <img src={detail[d.key]} alt={d.label} className="w-full rounded-lg border border-[#1a2823]"/>
                  </div>
                ) : null
              ))}
            </div>

            {detail.status === 'pending' && (
              <>
                <div className="mb-3">
                  <label className="text-[10px] text-gray-400 uppercase">Rejection Reason (only if rejecting)</label>
                  <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Blurry ID photo…" className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]"/>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => review(detail.user_id, 'reject')} className="px-4 py-2 rounded border border-red-500/40 text-red-400 text-sm font-semibold hover:bg-red-500/10 inline-flex items-center gap-1.5"><X size={13}/> Reject</button>
                  <button onClick={() => review(detail.user_id, 'approve')} className="btn-green px-4 py-2 rounded text-sm font-semibold inline-flex items-center gap-1.5"><Check size={13}/> Approve</button>
                </div>
              </>
            )}
            {detail.status !== 'pending' && (
              <div className="p-3 rounded bg-[#050807] border border-[#1a2823] text-xs text-gray-400">
                Reviewed on {detail.reviewed_at ? new Date(detail.reviewed_at).toLocaleString() : '—'}. Status: <span className="font-bold text-white">{detail.status.toUpperCase()}</span>.
                {detail.rejection_reason && <div className="mt-1">Reason: {detail.rejection_reason}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</div>
    <div className="text-sm text-white mt-0.5">{value || '—'}</div>
  </div>
);

export default AdminKYC;
